import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, EyeIcon, ShieldIcon, CheckIcon, XIcon } from 'lucide-react';
// Mock permissions data
const permissions = [{
  id: 1,
  name: 'View Dashboard',
  category: 'Dashboard'
}, {
  id: 2,
  name: 'View Reports',
  category: 'Reports'
}, {
  id: 3,
  name: 'Export Reports',
  category: 'Reports'
}, {
  id: 4,
  name: 'Create Users',
  category: 'User Management'
}, {
  id: 5,
  name: 'Edit Users',
  category: 'User Management'
}, {
  id: 6,
  name: 'Delete Users',
  category: 'User Management'
}, {
  id: 7,
  name: 'Create Roles',
  category: 'Role Management'
}, {
  id: 8,
  name: 'Edit Roles',
  category: 'Role Management'
}, {
  id: 9,
  name: 'Delete Roles',
  category: 'Role Management'
}, {
  id: 10,
  name: 'View Audit Logs',
  category: 'Audit'
}, {
  id: 11,
  name: 'View Merchants',
  category: 'Merchants'
}, {
  id: 12,
  name: 'Edit Merchants',
  category: 'Merchants'
}, {
  id: 13,
  name: 'Approve Transactions',
  category: 'Transactions'
}, {
  id: 14,
  name: 'View Transactions',
  category: 'Transactions'
}, {
  id: 15,
  name: 'Manage Settings',
  category: 'System'
}];
// Mock roles data
const generateMockRoles = () => {
  return [{
    id: 1,
    name: 'Administrator',
    description: 'Full system access',
    usersCount: 2,
    permissions: permissions.map(p => p.id),
    createdAt: '2023-01-15'
  }, {
    id: 2,
    name: 'Manager',
    description: 'Can manage users and view reports',
    usersCount: 5,
    permissions: [1, 2, 3, 4, 5, 10, 11, 14],
    createdAt: '2023-02-20'
  }, {
    id: 3,
    name: 'Operator',
    description: 'Day-to-day operations',
    usersCount: 8,
    permissions: [1, 2, 11, 13, 14],
    createdAt: '2023-03-10'
  }, {
    id: 4,
    name: 'Viewer',
    description: 'Read-only access',
    usersCount: 15,
    permissions: [1, 2, 10, 11, 14],
    createdAt: '2023-04-05'
  }];
};
const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'create'>('view');
  useEffect(() => {
    // Load mock data
    setRoles(generateMockRoles());
  }, []);
  // Filter roles based on search term
  const filteredRoles = roles.filter(role => role.name.toLowerCase().includes(searchTerm.toLowerCase()) || role.description.toLowerCase().includes(searchTerm.toLowerCase()));
  const openModal = (mode: 'view' | 'edit' | 'create', role?: any) => {
    setModalMode(mode);
    setSelectedRole(role || {
      permissions: []
    });
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRole(null);
  };
  const isPermissionSelected = (permissionId: number) => {
    return selectedRole?.permissions.includes(permissionId);
  };
  const togglePermission = (permissionId: number) => {
    if (modalMode === 'view') return;
    const newPermissions = isPermissionSelected(permissionId) ? selectedRole.permissions.filter(id => id !== permissionId) : [...selectedRole.permissions, permissionId];
    setSelectedRole({
      ...selectedRole,
      permissions: newPermissions
    });
  };
  // Group permissions by category
  const permissionsByCategory = permissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {});
  return <div className="space-y-6" data-id="element-248">
      <div className="flex justify-between items-center" data-id="element-249">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-250">
          Role Management
        </h1>
        <button onClick={() => openModal('create')} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-251">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-252" />
          Add Role
        </button>
      </div>
      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-253">
        <div className="max-w-xs" data-id="element-254">
          <div className="relative rounded-md shadow-sm" data-id="element-255">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-256">
              <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-257" />
            </div>
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search roles..." data-id="element-258" />
          </div>
        </div>
      </div>
      {/* Roles Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-259">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-260">
          <thead className="bg-gray-50" data-id="element-261">
            <tr data-id="element-262">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-263">
                Role
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-264">
                Description
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-265">
                Users
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-266">
                Created
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-267">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-268">
            {filteredRoles.map(role => <tr key={role.id} className="hover:bg-gray-50" data-id="element-269">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-270">
                  <div className="flex items-center" data-id="element-271">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600" data-id="element-272">
                      <ShieldIcon className="h-5 w-5" data-id="element-273" />
                    </div>
                    <div className="ml-4" data-id="element-274">
                      <div className="text-sm font-medium text-gray-900" data-id="element-275">
                        {role.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-276">
                  <div className="text-sm text-gray-900" data-id="element-277">
                    {role.description}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-278">
                  <div className="text-sm text-gray-900" data-id="element-279">
                    {role.usersCount} users
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-280">
                  {role.createdAt}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-281">
                  <div className="flex justify-end space-x-2" data-id="element-282">
                    <button onClick={() => openModal('view', role)} className="text-gray-400 hover:text-gray-500" data-id="element-283">
                      <EyeIcon className="h-4 w-4" data-id="element-284" />
                    </button>
                    <button onClick={() => openModal('edit', role)} className="text-blue-400 hover:text-blue-500" data-id="element-285">
                      <EditIcon className="h-4 w-4" data-id="element-286" />
                    </button>
                    <button className="text-red-400 hover:text-red-500" data-id="element-287">
                      <TrashIcon className="h-4 w-4" data-id="element-288" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {/* Role Modal */}
      {isModalOpen && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-289">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-290">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-291">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-292"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-293">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-294">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-295">
                <div className="sm:flex sm:items-start" data-id="element-296">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-297">
                    <ShieldIcon className="h-6 w-6 text-blue-600" data-id="element-298" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-299">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-300">
                      {modalMode === 'view' ? 'Role Details' : modalMode === 'edit' ? 'Edit Role' : 'Create Role'}
                    </h3>
                    <div className="mt-4 space-y-4" data-id="element-301">
                      <div data-id="element-302">
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-303">
                          Role Name
                        </label>
                        <input type="text" id="name" value={selectedRole?.name || ''} readOnly={modalMode === 'view'} className={`mt-1 block w-full shadow-sm sm:text-sm rounded-md ${modalMode === 'view' ? 'bg-gray-50 border-gray-300' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-304" />
                      </div>
                      <div data-id="element-305">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-306">
                          Description
                        </label>
                        <textarea id="description" rows={2} value={selectedRole?.description || ''} readOnly={modalMode === 'view'} className={`mt-1 block w-full shadow-sm sm:text-sm rounded-md ${modalMode === 'view' ? 'bg-gray-50 border-gray-300' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-307" />
                      </div>
                      <div data-id="element-308">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-309">
                          Permissions
                        </label>
                        <div className="max-h-80 overflow-y-auto border border-gray-300 rounded-md p-2 space-y-4" data-id="element-310">
                          {Object.entries(permissionsByCategory).map(([category, categoryPermissions]) => <div key={category} data-id="element-311">
                                <h4 className="text-sm font-medium text-gray-700 mb-2 pb-1 border-b" data-id="element-312">
                                  {category}
                                </h4>
                                <div className="space-y-2" data-id="element-313">
                                  {categoryPermissions.map((permission: any) => <div key={permission.id} className={`flex items-center p-2 rounded-md ${isPermissionSelected(permission.id) ? 'bg-blue-50' : 'hover:bg-gray-50'} ${modalMode !== 'view' ? 'cursor-pointer' : ''}`} onClick={() => togglePermission(permission.id)} data-id="element-314">
                                        <div className={`flex-shrink-0 h-5 w-5 rounded border ${isPermissionSelected(permission.id) ? 'bg-blue-600 border-blue-600 flex items-center justify-center' : 'border-gray-300'}`} data-id="element-315">
                                          {isPermissionSelected(permission.id) && <CheckIcon className="h-3 w-3 text-white" data-id="element-316" />}
                                        </div>
                                        <span className="ml-2 text-sm text-gray-700" data-id="element-317">
                                          {permission.name}
                                        </span>
                                      </div>)}
                                </div>
                              </div>)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-318">
                {modalMode !== 'view' && <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-319">
                    {modalMode === 'create' ? 'Create Role' : 'Save Changes'}
                  </button>}
                <button type="button" onClick={closeModal} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-320">
                  {modalMode === 'view' ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default RoleManagement;